import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SuperAdminLayout from '@/components/feature/SuperAdminLayout';
import SAHeader from '@/components/feature/SAHeader';
import LoadingState from '@/components/base/LoadingState';
import { supabase } from '@/lib/supabaseClient';
import { requireSuperAdmin } from '@/middleware/superAdminGuard';

interface AuditLog {
  id: string;
  tenant_id: string | null;
  user_id: string | null;
  user_email: string | null;
  action: string;
  entity: string | null;
  entity_id: string | null;
  entity_name: string | null;
  details: Record<string, unknown> | null;
  severity: string;
  created_at: string;
  tenant_name?: string;
}

const ACTION_META: Record<string, { bg: string; text: string; icon: string; label: string }> = {
  login:              { bg: 'bg-sky-50',     text: 'text-sky-700',     icon: 'ri-login-circle-line',       label: 'login' },
  impersonation_start:{ bg: 'bg-violet-50',  text: 'text-violet-700',  icon: 'ri-spy-line',                label: 'impersonate' },
  impersonation_end: { bg: 'bg-slate-100',  text: 'text-slate-500',   icon: 'ri-logout-circle-r-line',    label: 'end impersonation' },
  contract_signed:    { bg: 'bg-indigo-50',  text: 'text-indigo-700',  icon: 'ri-pen-nib-line',            label: 'contract signed' },
  addon_activated:    { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'ri-add-circle-line',         label: 'addon activated' },
  addon_disabled:     { bg: 'bg-red-50',     text: 'text-red-600',     icon: 'ri-indeterminate-circle-line',label: 'addon disabled' },
  addon_reactivated:  { bg: 'bg-teal-50',    text: 'text-teal-700',    icon: 'ri-refresh-line',            label: 'addon reactivated' },
  payment_created:    { bg: 'bg-amber-50',   text: 'text-amber-700',   icon: 'ri-money-dollar-circle-line',label: 'payment created' },
  payment_voided:     { bg: 'bg-rose-50',    text: 'text-rose-700',    icon: 'ri-forbid-line',             label: 'payment voided' },
  payment_deleted:    { bg: 'bg-red-50',     text: 'text-red-600',     icon: 'ri-delete-bin-line',         label: 'payment deleted' },
  payment_updated:    { bg: 'bg-amber-50',   text: 'text-amber-700',   icon: 'ri-edit-line',               label: 'payment updated' },
  job_created:        { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'ri-briefcase-line',          label: 'job created' },
  job_updated:        { bg: 'bg-amber-50',   text: 'text-amber-700',   icon: 'ri-edit-line',               label: 'job updated' },
  job_deleted:        { bg: 'bg-red-50',     text: 'text-red-600',     icon: 'ri-delete-bin-line',         label: 'job deleted' },
  module_activated:   { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'ri-toggle-line',         label: 'module on' },
  module_deactivated: { bg: 'bg-slate-100',  text: 'text-slate-500',   icon: 'ri-toggle-line',         label: 'module off' },
  domain_approved:    { bg: 'bg-green-50',  text: 'text-green-700',   icon: 'ri-check-double-line',       label: 'domain approved' },
  domain_rejected:    { bg: 'bg-rose-50',    text: 'text-rose-700',    icon: 'ri-close-circle-line',       label: 'domain rejected' },
  domain_active:      { bg: 'bg-sky-50',     text: 'text-sky-700',     icon: 'ri-global-line',             label: 'domain active' },
  tenant_created:     { bg: 'bg-blue-50',    text: 'text-blue-700',    icon: 'ri-building-2-line',         label: 'tenant created' },
  tenant_updated:     { bg: 'bg-amber-50',   text: 'text-amber-700',   icon: 'ri-edit-line',               label: 'tenant updated' },
  client_created:     { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'ri-user-add-line',           label: 'client created' },
  client_updated:     { bg: 'bg-amber-50',   text: 'text-amber-700',   icon: 'ri-edit-line',               label: 'client updated' },
  contract_created:   { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'ri-file-add-line',           label: 'contract created' },
  contract_signed_reset:{bg:'bg-amber-50', text: 'text-amber-700',   icon: 'ri-refresh-line',            label: 'signature reset' },
  default:            { bg: 'bg-slate-100',  text: 'text-slate-500',   icon: 'ri-file-list-line',          label: '' },
};

const SEVERITY_DOT: Record<string, string> = {
  info: 'bg-slate-300',
  warning: 'bg-amber-400',
  critical: 'bg-red-500',
};

function getActionMeta(action: string) {
  return ACTION_META[action] ?? ACTION_META.default;
}

function formatDate(iso: string) {
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString('es', { day: 'numeric', month: 'short', year: '2-digit' }),
    time: d.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' }),
  };
}

function formatDetails(details: Record<string, unknown> | null): string | null {
  if (!details) return null;
  const entries = Object.entries(details).slice(0, 3);
  if (entries.length === 0) return null;
  return entries.map(([k, v]) => `${k}: ${String(v).slice(0, 40)}`).join(' · ');
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).catch(() => {});
}

const PAGE_SIZE = 50;

export default function SAAuditLogs() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [tenantFilter, setTenantFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [tenants, setTenants] = useState<{ id: string; name: string }[]>([]);
  const [users, setUsers] = useState<{ id: string; email: string | null }[]>([]);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);

    let q = supabase
      .from('audit_logs')
      .select('*')
      .order('created_at', { ascending: false })
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

    if (actionFilter !== 'all') q = q.eq('action', actionFilter);
    if (tenantFilter !== 'all') q = q.eq('tenant_id', tenantFilter);
    if (userFilter !== 'all')   q = q.eq('user_id', userFilter);
    if (severityFilter !== 'all') q = q.eq('severity', severityFilter);
    if (dateFrom) q = q.gte('created_at', `${dateFrom}T00:00:00Z`);
    if (dateTo)   q = q.lte('created_at', `${dateTo}T23:59:59Z`);

    const [logsRes, countRes, tenantsRes, usersRes] = await Promise.all([
      q,
      supabase.from('audit_logs').select('id', { count: 'exact', head: true }),
      supabase.from('saas_tenants').select('id, name'),
      supabase.from('audit_logs').select('user_id, user_email').not('user_id', 'is', null),
    ]);

    const tenantList = (tenantsRes.data ?? []) as { id: string; name: string }[];
    const tenantMap  = Object.fromEntries(tenantList.map((t) => [t.id, t.name]));

    // Unique users from audit logs
    const userMap = new Map<string, string | null>();
    (usersRes.data ?? []).forEach((u: { user_id: string; user_email: string | null }) => {
      if (u.user_id) userMap.set(u.user_id, u.user_email);
    });
    const userList = [...userMap.entries()].map(([id, email]) => ({ id, email }));

    const enriched: AuditLog[] = ((logsRes.data ?? []) as AuditLog[]).map((l) => ({
      ...l,
      tenant_name: tenantMap[l.tenant_id ?? ''] ?? (l.tenant_id ? `${l.tenant_id.slice(0, 8)}…` : 'Global'),
    }));

    setLogs(enriched);
    setTenants(tenantList);
    setUsers(userList);
    setTotalCount(countRes.count ?? 0);
    setLoading(false);
  }, [page, actionFilter, tenantFilter, userFilter, severityFilter, dateFrom, dateTo]);

  useEffect(() => {
    const init = async () => {
      const ok = await requireSuperAdmin();
      if (!ok) { navigate('/login'); return; }
      await fetchLogs();
    };
    init();
  }, [navigate, fetchLogs]);

  const handleCopy = (id: string, field: string) => {
    copyToClipboard(id);
    setCopiedId(field);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const uniqueActions = [...new Set(logs.map((l) => l.action).filter(Boolean))].sort();
  const totalPages    = Math.ceil(totalCount / PAGE_SIZE);

  // Stats
  const uniqueTenantCount = [...new Set(logs.map((l) => l.tenant_id).filter(Boolean))].length;
  const uniqueUserCount   = [...new Set(logs.map((l) => l.user_id).filter(Boolean))].length;
  const actionCounts      = logs.reduce<Record<string, number>>((acc, l) => {
    acc[l.action] = (acc[l.action] ?? 0) + 1;
    return acc;
  }, {});
  const topAction = Object.entries(actionCounts).sort((a, b) => b[1] - a[1])[0];
  const criticalCount = logs.filter((l) => l.severity === 'critical').length;

  return (
    <SuperAdminLayout>
      <SAHeader
        title="Audit Logs"
        subtitle="Registro global de acciones críticas en la plataforma — solo lectura"
      />

      <div className="px-4 md:px-8 py-4 md:py-7">

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-7">
          {[
            {
              label: 'Total logs',
              value: totalCount.toLocaleString(),
              icon: 'ri-file-list-3-line',
              color: 'text-slate-900',
              iconBg: 'bg-slate-100 text-slate-500',
            },
            {
              label: 'Esta página',
              value: logs.length,
              icon: 'ri-eye-line',
              color: 'text-slate-700',
              iconBg: 'bg-slate-100 text-slate-400',
            },
            {
              label: 'Tenants en logs',
              value: uniqueTenantCount,
              icon: 'ri-building-2-line',
              color: 'text-emerald-700',
              iconBg: 'bg-emerald-50 text-emerald-500',
            },
            {
              label: topAction ? `Top: ${topAction[0]}` : 'Top acción',
              value: topAction ? topAction[1] : '—',
              icon: topAction ? getActionMeta(topAction[0]).icon : 'ri-bar-chart-line',
              color: 'text-amber-700',
              iconBg: 'bg-amber-50 text-amber-500',
            },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl px-4 py-3.5 border border-slate-200 flex items-center gap-3">
              <div className={`w-9 h-9 flex items-center justify-center rounded-lg shrink-0 ${s.iconBg}`}>
                <i className={`${s.icon} text-base`}></i>
              </div>
              <div>
                <p className="text-slate-400 text-[11px] leading-tight">{s.label}</p>
                <p className={`text-xl font-bold leading-tight ${s.color}`}>{s.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Severity banner */}
        {criticalCount > 0 && (
          <div className="flex items-center gap-3 mb-5 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
            <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-100 text-red-600 shrink-0">
              <i className="ri-error-warning-line text-sm"></i>
            </span>
            <p className="text-red-700 text-sm">
              <strong>{criticalCount}</strong> log{criticalCount > 1 ? 's' : ''} con severidad <strong>critical</strong> en los últimos resultados.
            </p>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 mb-5 flex flex-wrap gap-3 items-center">
          {/* Search */}
          <div className="relative min-w-[200px] flex-1 max-w-xs">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400">
              <i className="ri-search-line text-sm"></i>
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar acción, entidad, tenant…"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-4 py-2 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-slate-400 transition-colors"
            />
          </div>

          {/* Action filter */}
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400 pointer-events-none">
              <i className="ri-flashlight-line text-sm"></i>
            </span>
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="bg-white border border-slate-200 rounded-lg pl-8 pr-3 py-2 text-slate-700 text-sm outline-none focus:border-slate-400 cursor-pointer"
            >
              <option value="all">Todas las acciones</option>
              {uniqueActions.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>

          {/* Tenant filter */}
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400 pointer-events-none">
              <i className="ri-building-2-line text-sm"></i>
            </span>
            <select
              value={tenantFilter}
              onChange={(e) => setTenantFilter(e.target.value)}
              className="bg-white border border-slate-200 rounded-lg pl-8 pr-3 py-2 text-slate-700 text-sm outline-none focus:border-slate-400 cursor-pointer"
            >
              <option value="all">Todos los tenants</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          {/* User filter */}
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400 pointer-events-none">
              <i className="ri-user-line text-sm"></i>
            </span>
            <select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              className="bg-white border border-slate-200 rounded-lg pl-8 pr-3 py-2 text-slate-700 text-sm outline-none focus:border-slate-400 cursor-pointer"
            >
              <option value="all">Todos los usuarios</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.email ?? u.id.slice(0, 8) + '…'}</option>
              ))}
            </select>
          </div>

          {/* Severity filter */}
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400 pointer-events-none">
              <i className="ri-error-warning-line text-sm"></i>
            </span>
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              className="bg-white border border-slate-200 rounded-lg pl-8 pr-3 py-2 text-slate-700 text-sm outline-none focus:border-slate-400 cursor-pointer"
            >
              <option value="all">Todas las severidades</option>
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="critical">Critical</option>
            </select>
          </div>

          {/* Date from */}
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400 pointer-events-none">
              <i className="ri-calendar-line text-sm"></i>
            </span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="bg-white border border-slate-200 rounded-lg pl-8 pr-3 py-2 text-slate-700 text-sm outline-none focus:border-slate-400 cursor-pointer"
            />
          </div>

          {/* Date to */}
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-xs">hasta</span>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400 pointer-events-none">
                <i className="ri-calendar-check-line text-sm"></i>
              </span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="bg-white border border-slate-200 rounded-lg pl-8 pr-3 py-2 text-slate-700 text-sm outline-none focus:border-slate-400 cursor-pointer"
              />
            </div>
          </div>

          {/* Clear filters */}
          {(search || actionFilter !== 'all' || tenantFilter !== 'all' || userFilter !== 'all' || severityFilter !== 'all' || dateFrom || dateTo) && (
            <button
              onClick={() => { setSearch(''); setActionFilter('all'); setTenantFilter('all'); setUserFilter('all'); setSeverityFilter('all'); setDateFrom(''); setDateTo(''); }}
              className="text-slate-400 hover:text-slate-700 text-xs flex items-center gap-1 cursor-pointer transition-colors whitespace-nowrap"
            >
              <i className="ri-close-line"></i> Limpiar filtros
            </button>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <LoadingState />
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-slate-100">
                <i className="ri-file-list-3-line text-slate-300 text-3xl"></i>
              </div>
              <div className="text-center">
                <p className="text-slate-600 font-semibold text-sm mb-1">Sin logs de auditoría</p>
                <p className="text-slate-400 text-xs max-w-xs leading-relaxed">
                  {search || actionFilter !== 'all' || tenantFilter !== 'all' || userFilter !== 'all' || severityFilter !== 'all' || dateFrom || dateTo
                    ? 'No hay logs que coincidan con los filtros aplicados.'
                    : 'Los audit logs aparecerán aquí cuando ocurran acciones críticas en la plataforma.'}
                </p>
              </div>
              {(search || actionFilter !== 'all' || tenantFilter !== 'all' || userFilter !== 'all' || severityFilter !== 'all' || dateFrom || dateTo) && (
                <button
                  onClick={() => { setSearch(''); setActionFilter('all'); setTenantFilter('all'); setUserFilter('all'); setSeverityFilter('all'); setDateFrom(''); setDateTo(''); }}
                  className="text-sm text-slate-500 hover:text-slate-800 border border-slate-200 rounded-lg px-4 py-2 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Limpiar filtros
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[960px]">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left text-slate-400 text-[11px] font-semibold px-5 py-3.5 uppercase tracking-wider">Fecha</th>
                    <th className="text-left text-slate-400 text-[11px] font-semibold px-5 py-3.5 uppercase tracking-wider">Severidad</th>
                    <th className="text-left text-slate-400 text-[11px] font-semibold px-5 py-3.5 uppercase tracking-wider">Tenant</th>
                    <th className="text-left text-slate-400 text-[11px] font-semibold px-5 py-3.5 uppercase tracking-wider">Usuario</th>
                    <th className="text-left text-slate-400 text-[11px] font-semibold px-5 py-3.5 uppercase tracking-wider">Acción</th>
                    <th className="text-left text-slate-400 text-[11px] font-semibold px-5 py-3.5 uppercase tracking-wider">Entidad</th>
                    <th className="text-left text-slate-400 text-[11px] font-semibold px-5 py-3.5 uppercase tracking-wider">Nombre / Detalles</th>
                    <th className="text-left text-slate-400 text-[11px] font-semibold px-5 py-3.5 uppercase tracking-wider">Entity ID</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {logs.map((log) => {
                    const meta  = getActionMeta(log.action);
                    const { date, time } = formatDate(log.created_at);
                    const rowKey = log.id;
                    const detailsText = formatDetails(log.details);
                    return (
                      <tr key={rowKey} className="hover:bg-slate-50/70 transition-colors">
                        {/* Fecha */}
                        <td className="px-5 py-3.5 whitespace-nowrap">
                          <p className="text-slate-700 text-xs font-medium">{date}</p>
                          <p className="text-slate-400 text-[10px]">{time}</p>
                        </td>

                        {/* Severity */}
                        <td className="px-5 py-3.5">
                          <span className={`inline-block w-2 h-2 rounded-full ${SEVERITY_DOT[log.severity] ?? 'bg-slate-300'}`}></span>
                          <span className="text-slate-500 text-[10px] ml-1.5 uppercase">{log.severity}</span>
                        </td>

                        {/* Tenant */}
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 flex items-center justify-center rounded-full bg-slate-200 text-slate-600 text-[10px] font-bold shrink-0">
                              {(log.tenant_name ?? '?').charAt(0).toUpperCase()}
                            </div>
                            <p className="text-slate-700 text-sm">{log.tenant_name}</p>
                          </div>
                        </td>

                        {/* User */}
                        <td className="px-5 py-3.5">
                          {log.user_email ? (
                            <p className="text-slate-600 text-xs truncate max-w-[140px]" title={log.user_email}>{log.user_email}</p>
                          ) : log.user_id ? (
                            <button
                              onClick={() => handleCopy(log.user_id, `uid-${log.id}`)}
                              className="flex items-center gap-1 group cursor-pointer"
                              title={log.user_id}
                            >
                              <span className="text-slate-400 text-xs font-mono group-hover:text-slate-700 transition-colors">
                                {log.user_id.slice(0, 8)}…
                              </span>
                              <span className="w-3.5 h-3.5 flex items-center justify-center">
                                <i className={`${copiedId === `uid-${log.id}` ? 'ri-check-line text-emerald-500' : 'ri-file-copy-line text-slate-300 group-hover:text-slate-500'} text-[11px] transition-colors`}></i>
                              </span>
                            </button>
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </td>

                        {/* Acción */}
                        <td className="px-5 py-3.5">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold ${meta.bg} ${meta.text}`}>
                            <i className={`${meta.icon} text-[11px]`}></i>
                            {log.action}
                          </span>
                        </td>

                        {/* Entidad */}
                        <td className="px-5 py-3.5">
                          {log.entity ? (
                            <div className="flex items-center gap-1.5">
                              <span className="inline-block px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-xs font-medium">{log.entity}</span>
                            </div>
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </td>

                        {/* Nombre + Details */}
                        <td className="px-5 py-3.5 max-w-[220px]">
                          {log.entity_name ? (
                            <p className="text-slate-600 text-xs truncate" title={log.entity_name}>{log.entity_name}</p>
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                          {detailsText && (
                            <p className="text-slate-400 text-[10px] truncate mt-0.5" title={detailsText}>{detailsText}</p>
                          )}
                        </td>

                        {/* Entity ID */}
                        <td className="px-5 py-3.5">
                          {log.entity_id ? (
                            <button
                              onClick={() => handleCopy(log.entity_id, `eid-${log.id}`)}
                              className="flex items-center gap-1 group cursor-pointer"
                              title={log.entity_id}
                            >
                              <span className="text-slate-400 text-xs font-mono group-hover:text-slate-700 transition-colors">
                                {log.entity_id.slice(0, 8)}…
                              </span>
                              <span className="w-3.5 h-3.5 flex items-center justify-center">
                                <i className={`${copiedId === `eid-${log.id}` ? 'ri-check-line text-emerald-500' : 'ri-file-copy-line text-slate-300 group-hover:text-slate-500'} text-[11px] transition-colors`}></i>
                              </span>
                            </button>
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && totalCount > PAGE_SIZE && (
          <div className="flex items-center justify-between mt-4 px-1">
            <p className="text-slate-400 text-xs">
              Página {page + 1} de {totalPages} — {totalCount.toLocaleString()} logs en total
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-2 text-sm text-slate-500 hover:text-slate-900 border border-slate-200 rounded-lg transition-colors cursor-pointer disabled:opacity-40 whitespace-nowrap"
              >
                ← Anterior
              </button>
              <span className="text-slate-500 text-xs px-2">
                {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalCount)}
              </span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages - 1}
                className="px-3 py-2 text-sm text-slate-500 hover:text-slate-900 border border-slate-200 rounded-lg transition-colors cursor-pointer disabled:opacity-40 whitespace-nowrap"
              >
                Siguiente →
              </button>
            </div>
          </div>
        )}

        {/* Info note */}
        <div className="mt-4 flex items-start gap-2 p-3 bg-slate-50 border border-slate-200 rounded-xl">
          <span className="w-4 h-4 flex items-center justify-center text-slate-400 mt-0.5 shrink-0">
            <i className="ri-information-line text-xs"></i>
          </span>
          <p className="text-slate-400 text-xs leading-relaxed">
            Audit logs registran acciones críticas automáticamente: login, impersonación, firmas de contrato, pagos, cambios de estado, activación de módulos y dominios. Solo lectura — no se pueden eliminar desde aquí. Mostrando {PAGE_SIZE} registros por página, ordenados del más reciente al más antiguo.
          </p>
        </div>

      </div>
    </SuperAdminLayout>
  );
}